import ToolTipPopOver from '@atoms/ToolTipPopOver';
import { scale } from '@utils/normalize';
import React, { useRef, useState } from 'react';
import {
  Pressable,
  View,
  Modal,
  Animated,
  TouchableWithoutFeedback,
  Dimensions,
  UIManager,
  findNodeHandle,
} from 'react-native';
import logCustomTooltip from './analyticsCustomTooltip';
import styles from './styles';

interface LogParams {
  [key: string]: string | number;
}

interface ControlledTooltipProps {
  translateString?: string;
  children: React.ReactNode;
  logParams?: LogParams;
}

const ControlledTooltip: React.FC<ControlledTooltipProps> = ({ translateString = '', children, logParams = {} }) => {
  const [visible, setVisible] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const [top, setTop] = useState(0);
  const childRef = useRef<View>(null);

  const openTooltip = () => {
    const node = findNodeHandle(childRef.current);
    if (node) {
      UIManager.measureInWindow(node, (_x, y, _w, h) => {
        logCustomTooltip(logParams);
        setTop(y + h + 4); // Position below the child
        setVisible(true);
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }).start();
      });
    }
  };

  const closeTooltip = () => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 150,
      useNativeDriver: true,
    }).start(() => setVisible(false));
  };

  const screenWidth = Dimensions.get('window').width;
  const tooltipWidth = screenWidth - scale(24);
  const left = (screenWidth - tooltipWidth) / 2;
  const animatedStyle = {
    position: 'absolute',
    top,
    left,
    width: tooltipWidth,
    opacity: fadeAnim,
    transform: [
      {
        translateY: fadeAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [10, 0],
        }),
      },
    ],
  };
  return (
    <>
      <Pressable ref={childRef} onPress={openTooltip}>
        {children}
      </Pressable>

      {visible && (
        <Modal transparent animationType="none" onRequestClose={closeTooltip}>
          <TouchableWithoutFeedback onPress={closeTooltip}>
            <View style={styles.fullscreenOverlay}>
              <Animated.View style={[styles.tooltipContainerStyle, animatedStyle]}>
                <ToolTipPopOver translateString={translateString} setOpen={setVisible} />
              </Animated.View>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </>
  );
};

export default React.memo(ControlledTooltip);
